<section class="hero">
  <div class="content">
    <span class="hero-overline">{{ pageTexts.HERO_TITLE }}</span>
    <h1 class="hero-title">{{ pageTexts.HERO_SUBTITLE }}</h1>
    <div class="buttons">
      <app-button (click)="navigateToCatalog()" variant="outlined">{{ pageTexts.CATALOG_BUTTON }}</app-button>
      <app-button (click)="navigateToEncargo()" variant="primary">{{ pageTexts.QUOTE_BUTTON }}</app-button>
    </div>
  </div>
  <div class="image">
    <img src="/assets/images/hero-image.webp" alt="Personaje 3D" width="494" height="505" loading="eager" fetchpriority="high">
  </div>
</section>

<section class="features">
  @for (feature of features; track feature.id) {
    <app-feature-card
      [icon]="feature.icon"
      [title]="feature.title"
      [subtitle]="feature.subtitle">
    </app-feature-card>
  }
</section>

<section class="products">
  <h2 class="section-title">{{ pageTexts.CHECK_PRODUCTS }}</h2>
  <div class="products-grid">
    @if (isLoading()) {
      @for (item of skeletonItems; track $index) {
        <div class="product-card-skeleton">
          <div class="skeleton-image shimmer"></div>
          <div class="skeleton-info">
            <div class="skeleton-name shimmer"></div>
            <div class="skeleton-price shimmer"></div>
          </div>
        </div>
      }
    } @else {
      @for (product of products(); track product.id) {
        <app-product-card 
          [name]="product.name" 
          [status]="product.status"
          [price]="product | catalogPrice" 
          [imageUrl]="product.imageUrl"
          [slug]="product.slug || ''">
        </app-product-card>
      }
    }
  </div>

  <div class="action-container">
    <app-button (click)="navigateToCatalog()" variant="outlined" [fullWidth]="true">
      {{ pageTexts.CATALOG_BUTTON }}
    </app-button>
  </div>
</section>

<app-category-section ngSkipHydration [categories]="categories()"></app-category-section>

<section class="custom-request-section">
  <div class="custom-request-overlay"></div>
  <div class="custom-request-container">
    <div class="custom-request-content">
      <h2 class="custom-request-title">{{ pageTexts.CUSTOM_REQUEST.TITLE }}</h2>
      <p class="custom-request-subtitle">{{ pageTexts.CUSTOM_REQUEST.SUBTITLE }}</p>
      <app-button variant="squared">{{ pageTexts.CUSTOM_REQUEST.BUTTON }}</app-button>
    </div>
    <div class="custom-request-image">
      <img src="/assets/images/3d-cube-mesh.webp" alt="Malla de cubo 3D" width="1000" height="666" loading="lazy" decoding="async">
    </div>
  </div>
</section>

<section class="featured-collection">
  <div class="bg-decoration"></div>
  <div class="container">
    <div class="text-content">
      <h2 class="title">{{ pageTexts.MANDALAS_SECTION.TITLE }}</h2>
      <p class="description">{{ pageTexts.MANDALAS_SECTION.PHRASES.join('  ✦  ') }}</p>
      <app-button variant="outlined">{{ pageTexts.MANDALAS_SECTION.BUTTON }}</app-button>
    </div>

    <div class="card-content">
      @if (isLoading()) {
        <div class="product-card-skeleton featured-skeleton">
          <div class="skeleton-image shimmer"></div>
          <div class="skeleton-info">
            <div class="skeleton-name shimmer"></div>
            <div class="skeleton-price shimmer"></div>
          </div>
        </div>
      } @else if (plantProduct()) {
        <app-product-card 
          [name]="plantProduct()!.name"
          [status]="plantProduct()!.status"
          [price]="plantProduct()! | catalogPrice"
          [imageUrl]="plantProduct()!.imageUrl"
          [isFeatured]="true"
          [slug]="plantProduct()!.slug || ''">
        </app-product-card>
      }
    </div>
  </div>
</section>

<section class="designer-section">
  <div class="designer-container">
    
    <div class="designer-top-row">
      <div class="designer-image-column">
        <div class="designer-card">
          <div class="designer-image-wrapper">
            <img src="/assets/images/argentus-photo.webp" alt="Diseñadora Argentus Mundo 3D" width="990" height="990" loading="lazy" decoding="async">
          </div>
        </div>
      </div>

      <div class="designer-content-right">
        <div class="text-block">
          <h2 class="designer-title">{{ pageTexts.DESIGNER_SECTION.TITLE }}</h2>
          <p class="designer-body">
            {{ pageTexts.DESIGNER_SECTION.DESCRIPTION }}
          </p>
        </div>
      </div>
    </div>

    <div class="designer-bottom-row">
      <section class="about-panel" aria-labelledby="about-title">
        <header class="about-header">
          <span class="about-overline">Argentus Mundo 3D</span>

          <h2 id="about-title" class="about-title">
            Quiénes somos
          </h2>
        </header>

        <div class="about-grid">
          <div class="about-story">
            <div class="about-story-content">
              <p class="about-lead">
                Transformamos ideas en objetos reales mediante diseño e impresión 3D.
              </p>

              <p class="about-description">
                Acompañamos cada proyecto desde el concepto inicial hasta la pieza
                terminada, combinando creatividad, precisión y fabricación a medida.
                Cada creación tiene un propósito, una identidad y una historia propia.
              </p>
            </div>

            <div class="about-location">
              <span class="about-location-icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"></path>
                  <circle cx="12" cy="10" r="2.5"></circle>
                </svg>
              </span>

              <span>{{ pageTexts.DESIGNER_SECTION.TAG_LABEL }}</span>
            </div>
          </div>

          <div class="about-principles">
            <article class="principle-card principle-card-mission">
              <header class="principle-header">
                <span class="principle-number">01</span>
                <h3>Misión</h3>
              </header>

              <p>
                Crear soluciones funcionales y personalizadas mediante impresión 3D,
                cuidando la calidad, el diseño y la precisión en cada etapa.
              </p>
            </article>

            <article class="principle-card principle-card-vision">
              <header class="principle-header">
                <span class="principle-number">02</span>
                <h3>Visión</h3>
              </header>

              <p>
                Impulsar una forma más creativa y responsable de fabricar, donde la
                tecnología permita convertir ideas únicas en piezas reales.
              </p>
            </article>
          </div>
        </div>
      </section>
    </div>
